import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, CircularProgress, Alert, Chip, Stack, Tabs, Tab, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox,
  MenuItem, IconButton, Tooltip, FormGroup, Switch,
} from '@mui/material';
import {
  NetworkCheck as NetIcon, Refresh as RefreshIcon, PlayArrow as RunIcon,
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Router as SwitchIcon,
  Radar as ScanIcon, CheckCircle as OkIcon,
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const SEV = {
  high:   { label: 'Высокая', color: 'error' },
  medium: { label: 'Средняя', color: 'warning' },
  low:    { label: 'Низкая',  color: 'info' },
};
const STATUS = {
  ok:          { label: 'OK',          color: 'success' },
  unreachable: { label: 'Недоступен',  color: 'error' },
  error:       { label: 'Ошибка',      color: 'warning' },
};
const METHODS = [
  { key: 'ping',  label: 'Пинг (доступность)' },
  { key: 'snmp',  label: 'SNMP (идентификация)' },
  { key: 'arp',   label: 'ARP (MAC)' },
  { key: 'ports', label: 'Порты коммутатора (FDB/LLDP)' },
];

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function Monitoring() {
  const { user } = useAuth();
  const canManage = user?.is_superuser || user?.permissions?.includes('monitoring.manage');
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [devices, setDevices] = useState([]);

  useEffect(() => {
    axios.get('/devices/').then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || r.data.devices || []);
      setDevices(list.map((d) => ({ id: d.id, name: d.name })));
    }).catch(() => {});
  }, []);
  const deviceName = useCallback((id) => devices.find((d) => d.id === id)?.name || `#${id}`, [devices]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <NetIcon color="primary" />
        <Typography variant="h4">Мониторинг сети</Typography>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Активный мониторинг двойника: доступность с локализацией поломки по цепочке (один корневой сигнал на участок),
        профили опроса без агента (ping/SNMP/ARP) и контроль портов коммутатора (подмена/перемещение/чужак).
        Изменения железа и портов попадают в очередь «Дрейф изменений» на разбор.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Инциденты доступности" />
        <Tab label="Профили опроса" />
        <Tab label="Порты коммутатора" />
      </Tabs>

      <TabPanel value={tab} index={0}><Incidents canManage={canManage} onError={setError} deviceName={deviceName} /></TabPanel>
      <TabPanel value={tab} index={1}><Profiles canManage={canManage} onError={setError} devices={devices} /></TabPanel>
      <TabPanel value={tab} index={2}><SwitchPorts canManage={canManage} onError={setError} devices={devices} /></TabPanel>
    </Box>
  );
}

// ---- Инциденты доступности --------------------------------------------------
function Incidents({ canManage, onError, deviceName }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('open');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/monitoring/incidents?status=${status}`);
      setRows(r.data || []);
    } catch (e) { onError(e.response?.data?.detail || e.message); } finally { setLoading(false); }
  }, [status, onError]);
  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    try { await axios.post('/monitoring/incidents/scan'); await load(); }
    catch (e) { onError(e.response?.data?.detail || e.message); } finally { setScanning(false); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Button size="small" variant={status === 'open' ? 'contained' : 'outlined'} onClick={() => setStatus('open')}>Открытые</Button>
        <Button size="small" variant={status === 'all' ? 'contained' : 'outlined'} onClick={() => setStatus('all')}>Все</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>Обновить</Button>
        {canManage && (
          <Button variant="contained" startIcon={<ScanIcon />} onClick={scan} disabled={scanning}>
            {scanning ? 'Сканирую…' : 'Сканировать сейчас'}
          </Button>
        )}
      </Stack>
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        : rows.length === 0 ? <Alert severity="success" icon={<OkIcon />}>Открытых инцидентов нет — связь в норме.</Alert>
        : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Важность</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Корень (где поломка)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Затронуто</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Сводка</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Начало</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map((it) => {
                  const s = SEV[it.severity] || { label: it.severity, color: 'default' };
                  return (
                    <TableRow key={it.id} hover>
                      <TableCell><Chip size="small" color={s.color} label={s.label} /></TableCell>
                      <TableCell>{it.root_device_name || (it.root_device_id ? `#${it.root_device_id}` : '—')}</TableCell>
                      <TableCell>
                        {it.affected_count > 0
                          ? <Tooltip title={(it.affected_names || []).join(', ')}><span>{it.affected_count} устр.</span></Tooltip>
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{it.summary}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={it.status === 'open' ? 'error' : 'success'}
                              label={it.status === 'open' ? 'открыт' : 'закрыт'} />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {it.started_at ? new Date(it.started_at).toLocaleString() : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
    </Box>
  );
}

// ---- Профили опроса ---------------------------------------------------------
const BLANK = { device_id: '', enabled: true, methods: ['ping'], target_ip: '', snmp_version: '2c', snmp_community: '', snmp_port: 161, interval_sec: 300 };

function Profiles({ canManage, onError, devices }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get('/monitoring/profiles'); setRows(r.data || []); }
    catch (e) { onError(e.response?.data?.detail || e.message); } finally { setLoading(false); }
  }, [onError]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(BLANK); setEditing(false); setDlg(true); };
  const openEdit = (p) => {
    setForm({ device_id: p.device_id, enabled: p.enabled, methods: p.methods || [], target_ip: p.target_ip || '',
      snmp_version: p.snmp_version || '2c', snmp_community: '', snmp_port: p.snmp_port || 161, interval_sec: p.interval_sec || 300 });
    setEditing(true); setDlg(true);
  };
  const save = async () => {
    try {
      const body = { ...form };
      if (!body.snmp_community) delete body.snmp_community; // don't overwrite with blank
      await axios.post('/monitoring/profiles', body);
      setDlg(false); await load();
    } catch (e) { onError(e.response?.data?.detail || e.message); }
  };
  const run = async (id) => { try { await axios.post(`/monitoring/profiles/${id}/run`); await load(); } catch (e) { onError(e.response?.data?.detail || e.message); } };
  const del = async (id) => { try { await axios.delete(`/monitoring/profiles/${id}`); await load(); } catch (e) { onError(e.response?.data?.detail || e.message); } };
  const toggleMethod = (k) => setForm((f) => ({ ...f, methods: f.methods.includes(k) ? f.methods.filter((m) => m !== k) : [...f.methods, k] }));

  return (
    <Box>
      <Stack direction="row" sx={{ mb: 2 }} alignItems="center">
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<RefreshIcon />} sx={{ mr: 1 }} onClick={load}>Обновить</Button>
        {canManage && <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Добавить профиль</Button>}
      </Stack>
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        : rows.length === 0 ? <Alert severity="info">Профилей нет. Добавьте профиль для устройства без агента (ИБП, контроллер, коммутатор).</Alert>
        : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Устройство</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Методы</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>IP опроса</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>SNMP</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Интервал</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Последний опрос</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Действия</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map((p) => {
                  const st = STATUS[p.last_status] || (p.last_status ? { label: p.last_status, color: 'default' } : null);
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.device_name || `#${p.device_id}`}{!p.enabled && <Chip size="small" sx={{ ml: 1 }} label="выкл" />}</TableCell>
                      <TableCell><Stack direction="row" spacing={0.5} flexWrap="wrap">{(p.methods || []).map((m) => <Chip key={m} size="small" label={m} />)}</Stack></TableCell>
                      <TableCell>{p.target_ip || '—'}</TableCell>
                      <TableCell>{p.snmp_community_set ? <Chip size="small" color="success" label={`v${p.snmp_version}`} /> : '—'}</TableCell>
                      <TableCell>{p.interval_sec}с</TableCell>
                      <TableCell>
                        {st && <Chip size="small" color={st.color} label={st.label} />}
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          {p.last_run_at ? new Date(p.last_run_at).toLocaleString() : 'ещё не опрашивался'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {canManage && <Tooltip title="Опросить сейчас"><IconButton size="small" onClick={() => run(p.id)}><RunIcon fontSize="small" /></IconButton></Tooltip>}
                        {canManage && <Tooltip title="Изменить"><IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                        {canManage && <Tooltip title="Удалить"><IconButton size="small" color="error" onClick={() => del(p.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      <Dialog open={dlg} onClose={() => setDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Изменить профиль' : 'Новый профиль мониторинга'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Устройство" value={form.device_id} disabled={editing}
              onChange={(e) => setForm({ ...form, device_id: e.target.value })} fullWidth>
              {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
            <Box>
              <Typography variant="subtitle2">Методы опроса</Typography>
              <FormGroup>
                {METHODS.map((m) => (
                  <FormControlLabel key={m.key} control={<Checkbox checked={form.methods.includes(m.key)} onChange={() => toggleMethod(m.key)} />} label={m.label} />
                ))}
              </FormGroup>
            </Box>
            <TextField label="IP для опроса (если пусто — первый IP устройства)" value={form.target_ip}
              onChange={(e) => setForm({ ...form, target_ip: e.target.value })} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField select label="SNMP версия" value={form.snmp_version} sx={{ width: 140 }}
                onChange={(e) => setForm({ ...form, snmp_version: e.target.value })}>
                <MenuItem value="1">v1</MenuItem><MenuItem value="2c">v2c</MenuItem>
              </TextField>
              <TextField label="SNMP community (только запись)" type="password" value={form.snmp_community}
                placeholder={editing ? '•••• оставьте пустым' : ''} onChange={(e) => setForm({ ...form, snmp_community: e.target.value })} fullWidth />
              <TextField label="Порт" type="number" value={form.snmp_port} sx={{ width: 110 }}
                onChange={(e) => setForm({ ...form, snmp_port: parseInt(e.target.value, 10) || 161 })} />
            </Stack>
            <TextField label="Интервал опроса, сек" type="number" value={form.interval_sec}
              onChange={(e) => setForm({ ...form, interval_sec: parseInt(e.target.value, 10) || 300 })} sx={{ width: 220 }} />
            <FormControlLabel control={<Switch checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />} label="Включён" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>Отмена</Button>
          <Button variant="contained" onClick={save} disabled={!form.device_id || form.methods.length === 0}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---- Порты коммутатора ------------------------------------------------------
function SwitchPorts({ canManage, onError, devices }) {
  const [deviceId, setDeviceId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [relearning, setRelearning] = useState(false);

  const load = useCallback(async (id) => {
    if (!id) { setData(null); return; }
    setLoading(true);
    try { const r = await axios.get(`/monitoring/switch-ports/${id}`); setData(r.data); }
    catch (e) { onError(e.response?.data?.detail || e.message); } finally { setLoading(false); }
  }, [onError]);
  useEffect(() => { load(deviceId); }, [deviceId, load]);

  const relearn = async () => {
    setRelearning(true);
    try { await axios.post(`/monitoring/switch-ports/${deviceId}/relearn`); await load(deviceId); }
    catch (e) { onError(e.response?.data?.detail || e.message); } finally { setRelearning(false); }
  };

  const fdb = data?.fdb || {};
  const lldp = data?.lldp || {};

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField select label="Коммутатор / устройство" value={deviceId} sx={{ minWidth: 280 }}
          onChange={(e) => setDeviceId(e.target.value)}>
          {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
        {canManage && deviceId && (
          <Button variant="contained" startIcon={<SwitchIcon />} onClick={relearn} disabled={relearning}>
            {relearning ? 'Опрос…' : 'Переобучить эталон (опросить сейчас)'}
          </Button>
        )}
      </Stack>

      {!deviceId ? <Alert severity="info">Выберите устройство-коммутатор с SNMP-профилем (метод «Порты коммутатора»).</Alert>
        : loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        : !data?.learned ? <Alert severity="warning">Эталон портов ещё не обучен. Нажмите «Переобучить эталон» или дождитесь планового опроса.</Alert>
        : (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" gutterBottom>Таблица MAC ↔ порт (FDB): {Object.keys(fdb).length}</Typography>
              {Object.keys(fdb).length === 0 ? <Alert severity="info">Записей нет.</Alert> : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead><TableRow><TableCell sx={{ fontWeight: 'bold' }}>MAC</TableCell><TableCell sx={{ fontWeight: 'bold' }}>Порт</TableCell></TableRow></TableHead>
                    <TableBody>{Object.entries(fdb).map(([mac, port]) => <TableRow key={mac} hover><TableCell>{mac}</TableCell><TableCell>{port}</TableCell></TableRow>)}</TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>Соседи LLDP: {Object.keys(lldp).length}</Typography>
              {Object.keys(lldp).length === 0 ? <Alert severity="info">Соседей нет (LLDP не отвечает или не настроен).</Alert> : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead><TableRow><TableCell sx={{ fontWeight: 'bold' }}>Лок. порт</TableCell><TableCell sx={{ fontWeight: 'bold' }}>Сосед (sysName)</TableCell><TableCell sx={{ fontWeight: 'bold' }}>Порт соседа</TableCell></TableRow></TableHead>
                    <TableBody>{Object.entries(lldp).map(([lp, nb]) => <TableRow key={lp} hover><TableCell>{lp}</TableCell><TableCell>{nb.sysname || '—'}</TableCell><TableCell>{nb.portid || '—'}</TableCell></TableRow>)}</TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Эталон обучён: {data.captured_at ? new Date(data.captured_at).toLocaleString() : '—'}.
              Отклонения (подмена/перемещение/чужак) попадают в очередь «Дрейф изменений».
            </Typography>
          </Stack>
        )}
    </Box>
  );
}

export default Monitoring;
